import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiClient, apiUrl, slackWebhookPath } from "../../constants.ts";
import {
  Button,
  Collapsible,
  CopyableValue,
  inputClass,
  PageSpinner,
  TokenDisplay,
} from "./components.tsx";
import { useAuth, useQuery } from "./db.ts";

export const SlackConfig = ({ id }: { id: string }) => {
  const token = useAuth().user?.refresh_token;
  const [botToken, setBotToken] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [appId, setAppId] = useState("");
  const [botUserId, setBotUserId] = useState("");
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const [bot] = data.bots;
  if (!bot) return <div>No bot found</div>;
  const webhookUrl = [apiUrl, slackWebhookPath, bot.id].join("/");
  return (
    <Collapsible title="Slack">
      <div className="flex flex-col gap-3 max-w-2xl">
        <div className="text-sm text-text-tertiary-light dark:text-text-tertiary">
          Configure your Slack app with Event Subscriptions enabled. Add the
          webhook URL below as the request URL and subscribe to bot events like
          <code>message.channels</code>, <code>message.im</code>,
          <code>app_mention</code>, and <code>reaction_added</code>.
        </div>
        <CopyableValue
          value={webhookUrl}
          label="Slack Events Request URL"
        />
        {!bot.slackBotToken
          ? (
            <>
              <div>
                <label>Bot token</label>
                <input
                  value={botToken}
                  onInput={(e) => setBotToken(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="xoxb-..."
                />
              </div>
              <div>
                <label>Signing secret</label>
                <input
                  value={signingSecret}
                  onInput={(e) => setSigningSecret(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="Slack signing secret"
                />
              </div>
              <div>
                <label>App ID (optional)</label>
                <input
                  value={appId}
                  onInput={(e) => setAppId(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="A1234567890"
                />
              </div>
              <div>
                <label>Bot user ID (optional)</label>
                <input
                  value={botUserId}
                  onInput={(e) => setBotUserId(e.currentTarget.value)}
                  className={inputClass}
                  placeholder="U1234567890"
                />
              </div>
              <Button
                className="w-fit"
                disabled={!token || !botToken || !signingSecret}
                onClick={() => {
                  if (!token) return;
                  toast.promise(
                    apiClient({
                      token,
                      endpoint: "set-slack-bot",
                      payload: {
                        botId: bot.id,
                        botToken,
                        signingSecret,
                        ...(appId ? { appId } : {}),
                        ...(botUserId ? { botUserId } : {}),
                      },
                    }),
                    {
                      loading: "Connecting Slack bot...",
                      success: (res) => {
                        if (res.success) {
                          setBotToken("");
                          setSigningSecret("");
                          setAppId("");
                          setBotUserId("");
                          return "Slack configured";
                        }
                        throw new Error(res.error);
                      },
                      error: (err) => `Failed: ${err.message}`,
                    },
                  );
                }}
              >
                Connect Slack
              </Button>
            </>
          )
          : (
            <div className="flex flex-col gap-2 max-w-md">
              <TokenDisplay label="Bot token" token={bot.slackBotToken} />
              {bot.slackSigningSecret && (
                <TokenDisplay
                  label="Signing secret"
                  token={bot.slackSigningSecret}
                />
              )}
              {bot.slackAppId && (
                <CopyableValue value={bot.slackAppId} label="App ID" />
              )}
              {bot.slackBotUserId && (
                <CopyableValue value={bot.slackBotUserId} label="Bot user ID" />
              )}
            </div>
          )}
      </div>
    </Collapsible>
  );
};
